import { useState, useEffect } from 'react';
import { healthApi } from '@/services/api';
import type { HealthCheck } from '@/types';

interface UseHealthCheckReturn {
  healthData: HealthCheck | null;
  lastSync: string | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook for fetching health check data on app mount
 * Returns the last sync timestamp from the backend
 */
const MAX_RETRIES = 4;
const RETRY_DELAY_MS = 3000;

export function useHealthCheck(): UseHealthCheckReturn {
  const [healthData, setHealthData] = useState<HealthCheck | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchHealthData = async () => {
      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          const data = await healthApi.check();
          if (!cancelled) {
            setHealthData(data);
            setError(null);
          }
          return;
        } catch (err) {
          if (cancelled) return;
          if (attempt < MAX_RETRIES) {
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
          } else {
            setError(err as Error);
            console.error('Failed to fetch health check data after retries:', err);
          }
        }
      }
      if (!cancelled) setIsLoading(false);
    };

    fetchHealthData().finally(() => {
      if (!cancelled) setIsLoading(false);
    });

    return () => { cancelled = true; };
  }, []); // Empty dependency array - fetch once on mount

  return {
    healthData,
    lastSync: healthData?.last_sync || null,
    isLoading,
    error,
  };
}
